import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  amount,
  periodPresets,
  printStatement,
} from "./FinancialStatementShared";
import { SbStyles, csvCell, escapeHtml } from "./SupplierBalanceReport";
import { TYPES, typeLabel } from "./SubcategoryListReport";

const PARTY_LABEL = {
  hr: "Staff Member",
  supplier: "Supplier",
  distributor: "Distributor",
  assets: "Asset",
};

// "none" stands for lines that carry no party.
const partyKey = (party) => (party.id === null ? "none" : String(party.id));

// A party's entries in the period, loaded when its row is opened.
const PartyLedger = ({ ledger }) => {
  if (!ledger || ledger.loading) {
    return (
      <div className="sb-ledger-note">
        <i className="fa fa-spinner fa-spin mg-r-6"></i>Loading entries...
      </div>
    );
  }
  if (ledger.error) {
    return <div className="sb-ledger-note sb-neg">{ledger.error}</div>;
  }
  return (
    <table className="sb-ledger">
      <thead>
        <tr>
          <th>Date</th>
          <th>Voucher</th>
          <th>Remarks</th>
          <th>Bill No</th>
          <th className="coa-num">Debit</th>
          <th className="coa-num">Credit</th>
          <th className="coa-num">Balance</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td colSpan={6} style={{ fontWeight: 600, color: "#2a2962" }}>
            Opening balance
          </td>
          <td className="coa-num" style={{ fontWeight: 600 }}>
            {amount(ledger.opening)}
          </td>
        </tr>
        {ledger.entries.length === 0 && (
          <tr>
            <td colSpan={7} className="sb-ledger-note">
              No entries in this period.
            </td>
          </tr>
        )}
        {ledger.entries.map((entry) => (
          <tr key={entry.id}>
            <td style={{ whiteSpace: "nowrap" }}>{formatDate(entry.date)}</td>
            <td style={{ whiteSpace: "nowrap" }}>
              <Link to={`/journal-voucher-details/${entry.voucher_id}`}>
                {entry.voucher_no}
              </Link>
              <span className="sb-sub" style={{ textTransform: "capitalize" }}>
                {entry.voucher_type}
              </span>
            </td>
            <td>
              {entry.remarks || (
                <span style={{ color: "#8b90a5" }} title="Voucher remarks">
                  {entry.voucher_remarks}
                </span>
              )}
            </td>
            <td>{entry.bill_number}</td>
            <td className="coa-num">
              {num(entry.debit) ? money(entry.debit) : ""}
            </td>
            <td className="coa-num">
              {num(entry.credit) ? money(entry.credit) : ""}
            </td>
            <td
              className={`coa-num ${num(entry.balance) < 0 ? "sb-neg" : ""}`}
              style={{ fontWeight: 600 }}
            >
              {amount(entry.balance)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SubcategoryLedgerReport = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [, thisMonthFrom, thisMonthTo] = periodPresets()[0];
  const [inputs, setInputs] = useState({
    from: searchParams.get("from") || thisMonthFrom,
    to: searchParams.get("to") || thisMonthTo,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState({});
  const [ledgers, setLedgers] = useState({});

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);
    setOpen({});
    setLedgers({});
    const query = { from: params.from, to: params.to };
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get(`/subcategory-ledger-report/${id}`, {
        params: query,
      });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the ledger. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const apply = (changes) => {
    const next = { ...inputs, ...changes };
    setInputs(next);
    fetchData(next);
  };

  const toggleParty = (party) => {
    const key = partyKey(party);
    const isOpen = !open[key];
    setOpen((values) => ({ ...values, [key]: isOpen }));
    if (!isOpen || ledgers[key]) return;

    setLedgers((values) => ({ ...values, [key]: { loading: true } }));
    Http.get(`/subcategory-ledger-report/${id}`, {
      params: { from: data.from, to: data.to, party: key },
    })
      .then((res) =>
        setLedgers((values) => ({ ...values, [key]: res.data.ledger })),
      )
      .catch((error) =>
        setLedgers((values) => ({
          ...values,
          [key]: {
            error:
              error.response?.data?.message || "Could not load the entries.",
          },
        })),
      );
  };

  const sub = data?.sub_ledger;
  const subTitle = sub
    ? sub.type === "new"
      ? sub.name
      : typeLabel(sub.type)
    : "";
  const partyLabel = (sub && PARTY_LABEL[sub.type]) || "Party";

  const parties = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (data ? data.parties : []).filter(
      (party) =>
        !query ||
        `${party.code || ""} ${party.name}`.toLowerCase().includes(query),
    );
  }, [data, search]);

  const totals = useMemo(() => {
    const sum = (key) => parties.reduce((s, p) => s + num(p[key]), 0);
    // The sub-ledger's own opening only belongs to the unfiltered total.
    const subOpening = search.trim() ? 0 : num(data?.totals.sub_ledger_opening);
    const opening = sum("opening") + subOpening;
    return {
      subOpening,
      opening,
      debit: sum("debit"),
      credit: sum("credit"),
      closing: opening + sum("credit") - sum("debit"),
      entries: parties.reduce((s, p) => s + p.entries, 0),
    };
  }, [parties, data, search]);

  const periodLabel = data
    ? `${formatDate(data.from)} – ${formatDate(data.to)}`
    : `${formatDate(inputs.from)} – ${formatDate(inputs.to)}`;

  const accountLabel = sub?.account
    ? `${sub.account.code} - ${sub.account.name}`
    : "";

  const exportCsv = () => {
    const lines = [
      [`Subcategory Ledger: ${subTitle}`],
      [accountLabel],
      [`${data.from} to ${data.to}`],
      [],
      [
        "Sl",
        "Code",
        partyLabel,
        "Opening",
        "Debit",
        "Credit",
        "Closing",
        "Entries",
        "Last Entry",
      ],
      ...(totals.subOpening
        ? [["", "", "Sub-ledger opening balance", totals.subOpening.toFixed(2)]]
        : []),
      ...parties.map((p, i) => [
        i + 1,
        p.code,
        p.name,
        num(p.opening).toFixed(2),
        num(p.debit).toFixed(2),
        num(p.credit).toFixed(2),
        num(p.closing).toFixed(2),
        p.entries,
        p.last_date,
      ]),
      [],
      [
        "",
        "",
        "Total",
        totals.opening.toFixed(2),
        totals.debit.toFixed(2),
        totals.credit.toFixed(2),
        totals.closing.toFixed(2),
        totals.entries,
      ],
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `subcategory-ledger-${id}_${data.from}_${data.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Sl</th><th>Code</th><th>${partyLabel}</th>
      <th class="num">Opening</th><th class="num">Debit</th>
      <th class="num">Credit</th><th class="num">Closing</th>`;
    const bodyHtml =
      (totals.subOpening
        ? `<tr><td></td><td></td><td>Sub-ledger opening balance</td>
            <td class="num">${amount(totals.subOpening)}</td><td></td><td></td><td></td></tr>`
        : "") +
      parties
        .map(
          (p, i) => `
        <tr>
          <td>${i + 1}</td>
          <td class="code">${escapeHtml(p.code)}</td>
          <td>${escapeHtml(p.name)}</td>
          <td class="num">${amount(p.opening)}</td>
          <td class="num">${money(p.debit)}</td>
          <td class="num">${money(p.credit)}</td>
          <td class="num">${amount(p.closing)}</td>
        </tr>`,
        )
        .join("") +
      `<tr class="grand">
        <td colspan="3">Total</td>
        <td class="num">${amount(totals.opening)}</td>
        <td class="num">${money(totals.debit)}</td>
        <td class="num">${money(totals.credit)}</td>
        <td class="num">${amount(totals.closing)}</td>
      </tr>`;

    printStatement({
      title: `Subcategory Ledger: ${escapeHtml(subTitle)}`,
      subtitle: `${escapeHtml(accountLabel)} &middot; ${periodLabel} &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: "Balances are credit less debit; amounts in brackets are debit balances.",
    });
  };

  const stat = (label, icon, value, subText, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{subText}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <SbStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Subcategory Ledger
              {sub && (
                <span className="mg-l-10" style={{ fontWeight: 400 }}>
                  <i
                    className={`fa ${TYPES[sub.type]?.icon || "fa-tag"} mg-r-6`}
                    aria-hidden="true"
                  ></i>
                  {subTitle}
                </span>
              )}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {accountLabel && (
                <>
                  <Link to={`/chart-of-account-ledger/${sub.account.id}`}>
                    {accountLabel}
                  </Link>{" "}
                  &middot;{" "}
                </>
              )}
              {periodLabel}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/sub-category-list" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Sub-ledgers
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !data}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Period
              </span>
              {sub && (
                <span className="coa-head-note">
                  <span
                    className={`coa-pill ${
                      sub.status === "active" ? "coa-pill-ok" : "coa-pill-muted"
                    } mg-r-6`}
                  >
                    {sub.status === "active" ? "Active" : "Inactive"}
                  </span>
                  {sub.remarks}
                </span>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-6">
                  <label>Period</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      name="from"
                      value={inputs.from}
                      onChange={handleChange}
                      max={inputs.to}
                      aria-label="From"
                    />
                    <input
                      type="date"
                      className="form-control"
                      name="to"
                      value={inputs.to}
                      onChange={handleChange}
                      min={inputs.from}
                      aria-label="To"
                    />
                  </div>
                  <PresetChips
                    presets={periodPresets()}
                    isActive={([, from, to]) =>
                      inputs.from === from && inputs.to === to
                    }
                    onPick={([, from, to]) => apply({ from, to })}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !inputs.from || !inputs.to}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Opening Balance",
                "fa-flag-o",
                amount(data.totals.opening),
                `As at ${formatDate(data.from)}`,
              )}
              {stat(
                "Debit",
                "fa-arrow-circle-o-down",
                money(data.totals.debit),
                `${data.totals.entries} entr${data.totals.entries === 1 ? "y" : "ies"} in period`,
              )}
              {stat(
                "Credit",
                "fa-arrow-circle-o-up",
                money(data.totals.credit),
                "Booked in this period",
              )}
              {stat(
                "Closing Balance",
                "fa-balance-scale",
                amount(data.totals.closing),
                `${data.parties.length} ${
                  sub.has_parties
                    ? `${partyLabel.toLowerCase()}${data.parties.length === 1 ? "" : "s"}`
                    : "ledger"
                } · as at ${formatDate(data.to)}`,
                num(data.totals.closing) < 0 ? "is-alert" : "is-ok",
              )}
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>
                {sub?.has_parties ? `Balances by ${partyLabel}` : "Balance"}
                <span className="coa-head-note mg-l-10">
                  Credit less debit · click a row to see its entries
                </span>
              </span>
              {data && sub.has_parties && data.parties.length > 0 && (
                <span className="sb-tools coa-no-print">
                  <div className="sb-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={`Search ${partyLabel.toLowerCase()}`}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label={`Search ${partyLabel}`}
                    />
                  </div>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The ledger could not be loaded.
              </div>
            ) : !parties.length && !totals.subOpening ? (
              <div className="coa-empty">
                <i className="fa fa-folder-open-o"></i>
                {data.parties.length
                  ? "Nothing matches the search."
                  : `Nothing posted to this sub-ledger up to ${formatDate(data.to)}.`}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="sb-table">
                  <thead>
                    <tr>
                      <th>Sl</th>
                      <th>Code</th>
                      <th>{partyLabel}</th>
                      <th className="coa-num">Opening</th>
                      <th className="coa-num">Debit</th>
                      <th className="coa-num">Credit</th>
                      <th className="coa-num">Closing</th>
                      <th>Entries</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {totals.subOpening !== 0 && (
                      <tr>
                        <td></td>
                        <td></td>
                        <td style={{ fontStyle: "italic", color: "#52566b" }}>
                          Sub-ledger opening balance
                        </td>
                        <td className="coa-num">{amount(totals.subOpening)}</td>
                        <td colSpan={4}></td>
                      </tr>
                    )}
                    {parties.map((party, i) => {
                      const key = partyKey(party);
                      const isOpen = !!open[key];
                      return (
                        <React.Fragment key={key}>
                          <tr className={`sb-row${isOpen ? " is-open" : ""}`}>
                            <td className="sb-sl">{i + 1}</td>
                            <td>
                              {party.code && (
                                <span className="coa-code">{party.code}</span>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="sb-name-btn"
                                onClick={() => toggleParty(party)}
                                aria-expanded={isOpen}
                              >
                                <i
                                  className={`fa fa-caret-${isOpen ? "down" : "right"} mg-r-6`}
                                  aria-hidden="true"
                                ></i>
                                {party.name}
                              </button>
                              {party.status === "inactive" && (
                                <span className="fs-inactive">Inactive</span>
                              )}
                            </td>
                            <td className="coa-num">{amount(party.opening)}</td>
                            <td className="coa-num">
                              {num(party.debit) ? money(party.debit) : ""}
                            </td>
                            <td className="coa-num">
                              {num(party.credit) ? money(party.credit) : ""}
                            </td>
                            <td
                              className={`coa-num ${num(party.closing) < 0 ? "sb-neg" : ""}`}
                              style={{ fontWeight: 700 }}
                            >
                              {amount(party.closing)}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {party.entries}
                              {party.last_date && (
                                <span className="sb-sub">
                                  Last {formatDate(party.last_date)}
                                </span>
                              )}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={8} className="sb-ledger-cell">
                                <PartyLedger ledger={ledgers[key]} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{ textAlign: "right" }}>
                        Total
                      </td>
                      <td className="coa-num">{amount(totals.opening)}</td>
                      <td className="coa-num">{money(totals.debit)}</td>
                      <td className="coa-num">{money(totals.credit)}</td>
                      <td className="coa-num">{amount(totals.closing)}</td>
                      <td>{totals.entries}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryLedgerReport;
